import { useCallback, useEffect, useState } from "react";
import { JourneyEditorService } from "../../services/journeyEditor/JourneyEditorService";
import {
  JourneyVersion,
  VersionDiff,
} from "../../services/journeyEditor/journeyEditorModels";
import { captureErrorToSentry } from "../../utilities/sentryHelper";

const journeyEditor = new JourneyEditorService();

/** A journey's history, one page at a time, and what each version differs by from now. */
export const useVersionHistory = (storeId: string, flowId: string) => {
  const [versions, setVersions] = useState<JourneyVersion[]>([]);
  const [nextAfter, setNextAfter] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [diff, setDiff] = useState<VersionDiff | null>(null);
  const [isDiffLoading, setIsDiffLoading] = useState<boolean>(false);

  const loadPage = useCallback(
    async (afterVersionId?: string) => {
      setIsLoading(true);
      setError("");
      try {
        const page = await journeyEditor.listVersions(
          storeId,
          flowId,
          afterVersionId
        );
        setVersions((loaded) =>
          afterVersionId ? [...loaded, ...page.versions] : page.versions
        );
        setNextAfter(page.next_after_version_id);
      } catch (err: any) {
        captureErrorToSentry(err, "Error in loading the version history");
        setError(err?.message || "Could not load the version history");
      } finally {
        setIsLoading(false);
      }
    },
    [storeId, flowId]
  );

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const loadDiff = useCallback(
    async (versionId: string) => {
      setDiff(null);
      setIsDiffLoading(true);
      try {
        setDiff(await journeyEditor.diffFromNow(storeId, flowId, versionId));
      } catch (err: any) {
        captureErrorToSentry(err, "Error in comparing a version");
      } finally {
        setIsDiffLoading(false);
      }
    },
    [storeId, flowId]
  );

  const loadMore = () => {
    if (nextAfter) {
      loadPage(nextAfter);
    }
  };

  return {
    versions,
    isLoading,
    error,
    hasMore: Boolean(nextAfter),
    loadMore,
    reload: loadPage,
    diff,
    isDiffLoading,
    loadDiff,
    restore: journeyEditor.restoreVersion.bind(journeyEditor),
  };
};
